/**
 * Register the service worker and take care of the lifecycle.
 *
 * @see https://web.dev/service-worker-lifecycle/
 * @see https://developer.chrome.com/docs/workbox/modules/workbox-window/
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Service_Worker_API/Using_Service_Workers
 */
import { Workbox } from "workbox-window"
import { initRegistrationRefresh } from "initializers/firebase"
import log from "features/common/logging/logging"

if ("serviceWorker" in navigator) {
  const wb = new Workbox("/packs/actual-service-worker.js", { scope: "/" })

  // Not used yet: Since we do not yet use caches, we can activate the new SW immediately.
  const showSkipWaitingPrompt = async (event) => {
    // Assuming the user accepted the update, set up a listener
    // that will reload the page as soon as the previously waiting
    // service worker has taken control.
    wb.addEventListener("controlling", () => {
      // At this point, reloading will ensure that the current
      // tab is loaded under the control of the new service worker.
      // Depending on your web app, you may want to auto-save or
      // persist transient state before triggering the reload.
      window.location.reload()
    })

    // When `event.wasWaitingBeforeRegister` is true, a previously
    // updated service worker is still waiting.
    // You may want to customize the UI prompt accordingly.
    const doUpdate = confirm(
      "Es ist ein neues Update verfügbar. Um dieses zu aktivieren, müssen alle Tabs neu geladen werden. Ungespeicherte Eingaben gehen dabei verloren. Jetzt neu laden?"
    )

    if (doUpdate) {
      wb.messageSkipWaiting()
    }
  }

  const sendCsrfTokenToSW = async () => {
    const csrfToken = document
      .querySelector("meta[name=csrf-token]")
      ?.getAttribute("content")

    if (csrfToken) {
      wb.messageSW({
        type: "SET_CSRF_TOKEN",
        payload: { csrfToken: csrfToken },
      })
        .then((csrfToken) => {
          log("CSRF Token send to Service Worker: " + csrfToken)
        })
        .catch((e) => log("CSRF Token could not be send to SW", e))
    } else {
      console.warn("CSRF Token not found in client!")
    }
  }

  // A service worker has installed but it's stuck in the waiting phase.
  wb.addEventListener("waiting", async (event) => {
    log(
      `A new service worker has installed, but it can't activate` +
        `until all tabs running the current version have fully unloaded ` +
        `(not refreshed!) or waiting is skipped forcefully.`
    )

    wb.messageSkipWaiting()
    // await showSkipWaitingPrompt(event)
  })

  // A new service worker has installed (for the first time)
  wb.addEventListener("installed", async (event) => {
    if (event.isUpdate) {
      log("Service Worker was updated!")

      // Tell the new SW directly the new CSRF token.
      // Otherwise only on next registration the CSRF is shared.
      await sendCsrfTokenToSW()
    } else {
      log("Service worker installed for the first time!")
    }
  })

  // The service worker has finished its `activate` callback
  wb.addEventListener("activated", (event) => {
    // `event.isUpdate` will be true if another version of the service
    // worker was controlling the page when this version was registered.
    if (!event.isUpdate) {
      log("Service worker activated for the first time!")

      // If your service worker is configured to precache assets, those
      // assets should all be available now.
    } else {
      log("Service worker activated!")
    }
  })

  // Send the CSRF token to the service worker, when requested (e.g. after a terminated service worker was restarted)
  wb.addEventListener("message", async (event) => {
    if (event.data.action === "request-csrf-token") {
      await sendCsrfTokenToSW()
    }
  })

  // The service worker has started controlling the page.
  // This happens only on second visit of the page: First visit the SW is discovered and installed,
  // but not controlling this client yet.
  wb.addEventListener("controlling", async (event) => {
    log(`The service worker is now controlling the page!`)
  })

  // Register the service worker after event listeners have been added.
  wb.register()
    .then(async (swRegistration) => {
      log("SW registered!")

      await initRegistrationRefresh(swRegistration.active)

      // TODO in case of error refresh service worker
      //swRegistration.update()
      await sendCsrfTokenToSW()

      // Check the version of the controlling service worker.
      wb.messageSW({ type: "GET_VERSION" }).then((swVersion) => {
        log("Checking service Worker version: " + swVersion)
      })
    })
    .catch((e) => log("SW could not be registered", e))
}
